import React from 'react';
import { getMfInstance, registerMfRemotes } from '@gcportal/base';
import type { PluginConfig, PluginSlots } from '@gc-bridge/models';

/**
 * Lazy components cached by remote id.
 *
 * A slot must always be represented by the *same* component type for the whole
 * lifetime of the app. Handing React a freshly created React.lazy for a slot
 * that is already mounted makes React unmount the old subtree and mount a new
 * one, which suspends - and because the shell wraps the entire embedded remote
 * in a single Suspense boundary, the whole remote gets torn down and the user
 * loses their work.
 */
const lazyComponentCache = new Map<string, PluginSlots[string]>();

/**
 * Plugin slots resolved during setup, keyed by the host remote they belong to.
 */
const slotsByHostRemote = new Map<string, PluginSlots>();

interface RemoteConfigWithPlugins {
  name: string;
  plugins?: PluginConfig[];
}

/**
 * Registers plugin remotes so they can be loaded later.
 * Call this during app setup, alongside embedded remote registration.
 */
export function registerPluginRemotes(plugins: PluginConfig[]): void {
  const remotes = plugins.map(({ remoteName, entry }) => ({
    name: remoteName,
    entry,
  }));

  if (remotes.length) {
    registerMfRemotes(remotes);
  }
}

/**
 * Extracts plugin configs from all remote configs that have them.
 */
export function extractPluginConfigs(
  remoteConfigs: Array<{ plugins?: PluginConfig[] }>
): PluginConfig[] {
  return remoteConfigs.flatMap((remote) => remote?.plugins ?? []);
}

/**
 * Resolves plugin components from registered MF remotes into a PluginSlots map.
 * Each slot maps to a React.lazy component that loads the remote on first render.
 *
 * The lazy components are cached by remote id, so calling this repeatedly for
 * the same plugin config always yields the same component types.
 */
export function resolvePlugins(plugins: PluginConfig[]): PluginSlots {
  const slots: PluginSlots = {};

  for (const plugin of plugins) {
    for (const component of plugin.components) {
      const remoteId = `${plugin.remoteName}/${component.exposedName}`;

      let LazyComponent = lazyComponentCache.get(remoteId);

      if (!LazyComponent) {
        LazyComponent = React.lazy(
          () =>
            getMfInstance().loadRemote(remoteId) as Promise<{
              default: React.ComponentType;
            }>
        );
        lazyComponentCache.set(remoteId, LazyComponent);
      }

      slots[component.slot] = LazyComponent;
    }
  }

  return slots;
}

/**
 * Registers every plugin remote declared in the shell config and resolves its
 * components into per-host slot maps.
 *
 * Called once during app setup: plugin remotes are only ever registered from
 * the initial config, so the slots must not be recomputed on later config
 * refreshes - see the comment on `lazyComponentCache`.
 */
export function initPlugins(remoteConfigs: RemoteConfigWithPlugins[]): void {
  const pluginConfigs = extractPluginConfigs(remoteConfigs);

  if (!pluginConfigs.length) {
    return;
  }

  registerPluginRemotes(pluginConfigs);

  for (const remote of remoteConfigs) {
    if (remote?.plugins?.length) {
      slotsByHostRemote.set(remote.name, resolvePlugins(remote.plugins));
    }
  }
}

/**
 * Returns the plugin slots injected into the given host remote, or undefined
 * when it has no plugins configured.
 */
export function getPluginSlots(hostRemoteName: string): PluginSlots | undefined {
  return slotsByHostRemote.get(hostRemoteName);
}
